
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { getAllEventStatistics } from '@/services/eventStatisticsService';
import { EventStatistics } from '@/lib/types';
import { loadProfessions } from '@/services/professionService';
import { Profession } from '@/lib/professionTypes';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#8DD1E1'];

const AnalyticsDashboard = () => {
  const [eventStats, setEventStats] = useState<EventStatistics[]>([]);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTimeRange, setSelectedTimeRange] = useState<'week' | 'month' | 'year'>('month');

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // In development, use local data
        if (import.meta.env.MODE === 'development') {
          const stats = getAllEventStatistics();
          setEventStats(stats);
          
          const loadedProfessions = await loadProfessions();
          setProfessions(loadedProfessions);
        } else {
          // TODO: Replace with real API endpoints when backend is ready
          const stats = getAllEventStatistics();
          setEventStats(stats);
          
          const loadedProfessions = await loadProfessions();
          setProfessions(loadedProfessions);
        }
      } catch (error) {
        console.error("Error loading analytics data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Prepare data for charts
  const viewsAndClicksData = eventStats.map(stat => ({
    name: stat.eventCode,
    views: stat.viewCount,
    clicks: stat.clickCount,
    ctr: stat.clickThroughRate,
  }));

  // Prepare data for pie chart based on professions
  const professionDistribution = professions
    .reduce((acc, prof) => {
      const category = prof.category;
      acc[category] = (acc[category] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    
  const professionPieData = Object.entries(professionDistribution).map(([name, value]) => ({
    name,
    value
  }));

  // Filter data based on time range
  const filterByTimeRange = (data: EventStatistics[]) => {
    const now = new Date();
    const cutoffDate = new Date();
    
    if (selectedTimeRange === 'week') {
      cutoffDate.setDate(now.getDate() - 7);
    } else if (selectedTimeRange === 'month') {
      cutoffDate.setMonth(now.getMonth() - 1);
    } else if (selectedTimeRange === 'year') {
      cutoffDate.setFullYear(now.getFullYear() - 1);
    }
    
    return data.filter(stat => {
      const updatedDate = new Date(stat.lastUpdated);
      return updatedDate >= cutoffDate;
    });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Esemény analitika áttekintés</CardTitle>
          <CardDescription>
            Megtekintsd a legfontosabb mutatószámokat az eseményekkel kapcsolatban
          </CardDescription>
          
          <div className="flex justify-end">
            <TabsList>
              <TabsTrigger 
                value="week" 
                onClick={() => setSelectedTimeRange('week')}
                className={selectedTimeRange === 'week' ? 'bg-primary text-primary-foreground' : ''}
              >
                Heti
              </TabsTrigger>
              <TabsTrigger 
                value="month" 
                onClick={() => setSelectedTimeRange('month')}
                className={selectedTimeRange === 'month' ? 'bg-primary text-primary-foreground' : ''}
              >
                Havi
              </TabsTrigger>
              <TabsTrigger 
                value="year" 
                onClick={() => setSelectedTimeRange('year')}
                className={selectedTimeRange === 'year' ? 'bg-primary text-primary-foreground' : ''}
              >
                Éves
              </TabsTrigger>
            </TabsList>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-muted-foreground">Adatok betöltése...</p>
            </div>
          ) : (
            <Tabs defaultValue="views">
              <TabsList className="mb-6">
                <TabsTrigger value="views">Megtekintések</TabsTrigger>
                <TabsTrigger value="engagement">Elköteleződés</TabsTrigger>
                <TabsTrigger value="professions">Szakterületek</TabsTrigger>
              </TabsList>
              
              <TabsContent value="views" className="space-y-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={viewsAndClicksData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="views" fill="#8884d8" name="Megtekintések" />
                      <Bar dataKey="clicks" fill="#82ca9d" name="Kattintások" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Összes megtekintés</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {eventStats.reduce((sum, stat) => sum + stat.viewCount, 0)}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Összes kattintás</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {eventStats.reduce((sum, stat) => sum + stat.clickCount, 0)}
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">Átlagos CTR</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {(eventStats.reduce((sum, stat) => sum + stat.clickThroughRate, 0) / eventStats.length).toFixed(2)}%
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
              
              <TabsContent value="engagement" className="space-y-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={viewsAndClicksData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="ctr" stroke="#ff7300" name="Átkattintási arány (%)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-4">Legnépszerűbb események</h3>
                  <div className="space-y-2">
                    {viewsAndClicksData
                      .sort((a, b) => b.views - a.views)
                      .slice(0, 5)
                      .map((event, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-muted rounded">
                          <span>{event.name}</span>
                          <span className="font-medium">{event.views} megtekintés</span>
                        </div>
                      ))}
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="professions" className="space-y-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={professionPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {professionPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                
                <div className="mt-6">
                  <h3 className="text-lg font-medium mb-4">Szakterületek megoszlása</h3>
                  <div className="space-y-2">
                    {professionPieData
                      .sort((a, b) => b.value - a.value)
                      .map((prof, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-muted rounded">
                          <span>{prof.name}</span>
                          <span className="font-medium">{prof.value} esemény</span>
                        </div>
                      ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
